import type {
  ServerTimingMetric,
  WebVitalConfidence,
  WebVitalDiagnostics
} from "@website/tracking/types/web_vital";
import { getElementSelector } from "@website/widget/scripts/web-vitals/selectors";
import {
  DiagnosticField,
  ExtendedNavigationTiming,
  LayoutShiftEntry,
  LayoutShiftSource
} from "@website/widget/scripts/web-vitals/types";

const mapServerTiming = (
  metrics: Array<PerformanceServerTiming> | undefined
): Array<ServerTimingMetric> | undefined =>
  metrics?.map((metric) => ({
    name: metric.name,
    duration: metric.duration,
    description: metric.description
  }));

const mapConfidence = (
  confidence: ExtendedNavigationTiming["confidence"] | undefined
): Partial<WebVitalConfidence> | undefined =>
  confidence
    ? {
        value: confidence.value,
        randomized_trigger_rate: confidence.randomizedTriggerRate
      }
    : undefined;

interface ClsDiagnosticsSource {
  culpritSource?: LayoutShiftSource;
  largestShift?: LayoutShiftEntry;
}

type LcpDiagnosticsSource = PerformanceEntry & {
  element?: HTMLElement | null;
  loadTime?: number;
  paintTime?: number;
  presentationTime?: number;
  renderTime?: number;
  size?: number;
  startTime: number;
  url?: string;
};

export const LCP_DIAGNOSTIC_FIELDS: Array<
  DiagnosticField<LcpDiagnosticsSource, WebVitalDiagnostics>
> = [
  {
    attributeKey: "element",
    sourceKey: "element",
    transform: (value) => getElementSelector(value as HTMLElement | null)
  },
  { attributeKey: "start_time", sourceKey: "startTime" },
  { attributeKey: "url", sourceKey: "url" },
  { attributeKey: "size", sourceKey: "size" },
  { attributeKey: "load_time", sourceKey: "loadTime" },
  { attributeKey: "render_time", sourceKey: "renderTime" },
  { attributeKey: "paint_time", sourceKey: "paintTime" },
  {
    attributeKey: "presentation_time",
    sourceKey: "presentationTime"
  }
];

export const FCP_DIAGNOSTIC_FIELDS: Array<
  DiagnosticField<PerformancePaintTiming, WebVitalDiagnostics>
> = [
  { attributeKey: "entry_type", sourceKey: "entryType" },
  { attributeKey: "name", sourceKey: "name" },
  { attributeKey: "start_time", sourceKey: "startTime" },
  { attributeKey: "paint_time", sourceKey: "paintTime" },
  {
    attributeKey: "presentation_time",
    sourceKey: "presentationTime"
  }
];

export const CLS_DIAGNOSTIC_FIELDS: Array<
  DiagnosticField<ClsDiagnosticsSource, WebVitalDiagnostics>
> = [
  {
    attributeKey: "culprit_element",
    compute: (source) => getElementSelector(source.culpritSource?.node)
  },
  {
    attributeKey: "largest_shift_value",
    compute: (source) => source.largestShift?.value
  },
  {
    attributeKey: "largest_shift_start_time",
    compute: (source) => source.largestShift?.startTime
  }
];

export const INP_DIAGNOSTIC_FIELDS: Array<
  DiagnosticField<PerformanceEventTiming, WebVitalDiagnostics>
> = [
  {
    attributeKey: "interaction_element",
    compute: (source) => getElementSelector(source.target as HTMLElement | null)
  },
  { attributeKey: "entry_type", sourceKey: "entryType" },
  { attributeKey: "name", sourceKey: "name" },
  { attributeKey: "start_time", sourceKey: "startTime" },
  { attributeKey: "duration", sourceKey: "duration" },
  {
    attributeKey: "processing_start",
    sourceKey: "processingStart"
  },
  {
    attributeKey: "processing_end",
    sourceKey: "processingEnd"
  },
  {
    attributeKey: "interaction_id",
    sourceKey: "interactionId"
  },
  {
    attributeKey: "cancelable",
    sourceKey: "cancelable"
  },
  {
    attributeKey: "input_delay",
    compute: (source) => source.processingStart - source.startTime
  },
  {
    attributeKey: "processing_time",
    compute: (source) => source.processingEnd - source.processingStart
  }
];

export const TTFB_DIAGNOSTIC_FIELDS: Array<
  DiagnosticField<ExtendedNavigationTiming, WebVitalDiagnostics>
> = [
  { attributeKey: "entry_type", sourceKey: "entryType" },
  { attributeKey: "type", sourceKey: "type" },
  { attributeKey: "start_time", sourceKey: "startTime" },
  { attributeKey: "duration", sourceKey: "duration" },
  {
    attributeKey: "initiator_type",
    sourceKey: "initiatorType"
  },
  {
    attributeKey: "next_hop_protocol",
    sourceKey: "nextHopProtocol"
  },
  {
    attributeKey: "render_blocking_status",
    sourceKey: "renderBlockingStatus"
  },
  {
    attributeKey: "content_encoding",
    sourceKey: "contentEncoding"
  },
  { attributeKey: "worker_start", sourceKey: "workerStart" },
  {
    attributeKey: "redirect_start",
    sourceKey: "redirectStart"
  },
  { attributeKey: "redirect_end", sourceKey: "redirectEnd" },
  { attributeKey: "fetch_start", sourceKey: "fetchStart" },
  {
    attributeKey: "domain_lookup_start",
    sourceKey: "domainLookupStart"
  },
  {
    attributeKey: "domain_lookup_end",
    sourceKey: "domainLookupEnd"
  },
  { attributeKey: "connect_start", sourceKey: "connectStart" },
  {
    attributeKey: "secure_connection_start",
    sourceKey: "secureConnectionStart"
  },
  { attributeKey: "connect_end", sourceKey: "connectEnd" },
  { attributeKey: "request_start", sourceKey: "requestStart" },
  {
    attributeKey: "final_response_headers_start",
    sourceKey: "finalResponseHeadersStart"
  },
  {
    attributeKey: "response_start",
    sourceKey: "responseStart"
  },
  { attributeKey: "response_end", sourceKey: "responseEnd" },
  {
    attributeKey: "dom_interactive",
    sourceKey: "domInteractive"
  },
  {
    attributeKey: "dom_content_loaded_event_start",
    sourceKey: "domContentLoadedEventStart"
  },
  {
    attributeKey: "dom_content_loaded_event_end",
    sourceKey: "domContentLoadedEventEnd"
  },
  { attributeKey: "dom_complete", sourceKey: "domComplete" },
  {
    attributeKey: "load_event_start",
    sourceKey: "loadEventStart"
  },
  { attributeKey: "load_event_end", sourceKey: "loadEventEnd" },
  {
    attributeKey: "unload_event_start",
    sourceKey: "unloadEventStart"
  },
  {
    attributeKey: "unload_event_end",
    sourceKey: "unloadEventEnd"
  },
  {
    attributeKey: "transfer_size",
    sourceKey: "transferSize"
  },
  {
    attributeKey: "encoded_body_size",
    sourceKey: "encodedBodySize"
  },
  {
    attributeKey: "decoded_body_size",
    sourceKey: "decodedBodySize"
  },
  {
    attributeKey: "response_status",
    sourceKey: "responseStatus"
  },
  {
    attributeKey: "server_timing",
    sourceKey: "serverTiming",
    transform: (value) =>
      mapServerTiming(value as Array<PerformanceServerTiming> | undefined)
  },
  {
    sourceKey: "confidence",
    attributeKey: "confidence",
    transform: (value) =>
      mapConfidence(value as ExtendedNavigationTiming["confidence"] | undefined)
  },
  // Time spent following redirects before the real request starts.
  {
    attributeKey: "custom_redirect_time_ms",
    compute: (source) =>
      source.redirectEnd > 0
        ? source.redirectEnd - source.redirectStart
        : undefined
  },
  // Time spent inside a service worker before the network fetch begins.
  {
    attributeKey: "custom_worker_time_ms",
    compute: (source) =>
      source.workerStart > 0
        ? source.fetchStart - source.workerStart
        : undefined
  },
  // DNS lookup cost for resolving the origin host.
  {
    attributeKey: "custom_dns_time_ms",
    compute: (source) =>
      source.domainLookupEnd > 0 || source.domainLookupStart > 0
        ? source.domainLookupEnd - source.domainLookupStart
        : undefined
  },
  // Whether the browser reused an existing connection instead of opening a new one.
  {
    attributeKey: "custom_connection_reused",
    compute: (source) =>
      source.connectStart > 0 || source.connectEnd > 0
        ? source.connectStart === source.connectEnd
        : undefined
  },
  // Raw TCP connect time, excluding TLS when HTTPS is used.
  {
    attributeKey: "custom_tcp_time_ms",
    compute: (source) =>
      source.connectStart > 0
        ? (source.secureConnectionStart > 0
            ? source.secureConnectionStart
            : source.connectEnd) - source.connectStart
        : undefined
  },
  // TLS handshake time after TCP is connected.
  {
    attributeKey: "custom_tls_time_ms",
    compute: (source) =>
      source.secureConnectionStart > 0
        ? source.connectEnd - source.secureConnectionStart
        : undefined
  },
  // Backend wait time from sending the request to receiving the first byte.
  {
    attributeKey: "custom_backend_time_ms",
    compute: (source) =>
      source.requestStart > 0 || source.responseStart > 0
        ? source.responseStart - source.requestStart
        : undefined
  },
  // Download time for the response body after the first byte arrives.
  {
    attributeKey: "custom_response_download_time_ms",
    compute: (source) =>
      source.responseStart > 0 || source.responseEnd > 0
        ? source.responseEnd - source.responseStart
        : undefined
  },
  // Heuristic flag for responses served from cache with no transferred bytes.
  {
    attributeKey: "custom_is_cached",
    compute: (source) =>
      source.transferSize === 0 && source.decodedBodySize > 0 ? true : undefined
  }
];
